import { createFrontOfficeInstance } from '../api-instance';

import {
  AssignCompanyCrmIntegrationRequestDto,
  CompanyChatAccessResponseDto,
  CompanyDepartmentDto,
  CompanyEmployeeDto,
  CompanyCrmRoutingResponseDto,
  CompanyCrmUsersMappingResponseDto,
  CompanyOutboundPolicyEventsFiltersDto,
  CompanyModulesResponseDto,
  CompanyOutboundPolicyEventsResponseDto,
  IAddEmployeeToCompanyDto,
  InviteCompanyEmployeeRequestDto,
  UpdateCompanyEmployeeRequestDto,
  UpdateCompanyChatAccessRequestDto,
  UpdateCompanyModuleRequestDto,
  UpdateCompanyCrmRoutingRequestDto,
  UpdateCompanyCrmUserMappingRequestDto,
} from './types';

export const companyApi = {
  addEmployee(companyId: string, data: IAddEmployeeToCompanyDto) {
    return inviteCompanyEmployee(companyId, data);
  },
};

export const getCompanyDepartments = (companyId: string) =>
  createFrontOfficeInstance<CompanyDepartmentDto[]>({
    url: `/api/companies/${companyId}/departments`,
    method: 'GET',
  });

export const createCompanyDepartment = (
  companyId: string,
  data: { name: string }
) =>
  createFrontOfficeInstance<CompanyDepartmentDto>({
    url: `/api/companies/${companyId}/departments`,
    method: 'POST',
    data,
  });

export const getCompanyEmployees = (companyId: string) =>
  createFrontOfficeInstance<CompanyEmployeeDto[]>({
    url: `/api/companies/${companyId}/employees`,
    method: 'GET',
  });

export const inviteCompanyEmployee = (
  companyId: string,
  data: InviteCompanyEmployeeRequestDto
) =>
  createFrontOfficeInstance<{ userId?: string; user_id?: string; inviteId?: string; invite_id?: string }>({
    url: `/api/companies/${companyId}/employees/invite`,
    method: 'POST',
    data,
  });

export const updateCompanyEmployee = (
  companyId: string,
  userId: string,
  data: UpdateCompanyEmployeeRequestDto
) =>
  createFrontOfficeInstance<CompanyEmployeeDto>({
    url: `/api/companies/${companyId}/employees/${encodeURIComponent(userId)}`,
    method: 'PATCH',
    data,
  });

export const deactivateCompanyEmployee = (companyId: string, userId: string) =>
  createFrontOfficeInstance<void>({
    url: `/api/companies/${companyId}/employees/${encodeURIComponent(userId)}/deactivate`,
    method: 'POST',
  });

export const resendCompanyEmployeeInvite = (companyId: string, userId: string) =>
  createFrontOfficeInstance<void>({
    url: `/api/companies/${companyId}/employees/${encodeURIComponent(userId)}/invite/resend`,
    method: 'POST',
  });

export const getCompanyChatAccess = (companyId: string) =>
  createFrontOfficeInstance<CompanyChatAccessResponseDto>({
    url: `/api/companies/${companyId}/chat-access`,
    method: 'GET',
  });

export const updateCompanyChatAccess = (
  companyId: string,
  data: UpdateCompanyChatAccessRequestDto
) =>
  createFrontOfficeInstance<void>({
    url: `/api/companies/${companyId}/chat-access`,
    method: 'PUT',
    data,
  });

export const assignCompanyCrmIntegration = (
  companyId: string,
  data: AssignCompanyCrmIntegrationRequestDto
) =>
  createFrontOfficeInstance<void>({
    url: `/api/companies/${companyId}/crm-integration`,
    method: 'PUT',
    data,
  });

export const getCompanyCrmRouting = (companyId: string) =>
  createFrontOfficeInstance<CompanyCrmRoutingResponseDto>({
    url: `/api/companies/${companyId}/crm-routing`,
    method: 'GET',
  });

export const getCompanyCrmUsers = (companyId: string) =>
  createFrontOfficeInstance<CompanyCrmUsersMappingResponseDto>({
    url: `/api/companies/${companyId}/crm-users`,
    method: 'GET',
  });

export const getCompanyModules = (companyId: string) =>
  createFrontOfficeInstance<CompanyModulesResponseDto>({
    url: `/api/companies/${companyId}/modules`,
    method: 'GET',
  });

export const getCompanyOutboundPolicyEvents = (
  companyId: string,
  filters: CompanyOutboundPolicyEventsFiltersDto = {}
) => {
  const params = new URLSearchParams();
  params.set('limit', String(filters.limit ?? 50));
  if (filters.channel_id) params.set('channel_id', filters.channel_id);
  if (filters.source) params.set('source', filters.source);
  if (filters.decision) params.set('decision', filters.decision);
  if (filters.reason) params.set('reason', filters.reason);

  return createFrontOfficeInstance<CompanyOutboundPolicyEventsResponseDto>({
    url: `/api/companies/${companyId}/outbound-policy/events?${params.toString()}`,
    method: 'GET',
  });
};

export const updateCompanyModule = (
  companyId: string,
  moduleKey: string,
  data: UpdateCompanyModuleRequestDto
) =>
  createFrontOfficeInstance<CompanyModulesResponseDto>({
    url: `/api/companies/${companyId}/modules/${moduleKey}`,
    method: 'PUT',
    data,
  });

export const updateCompanyCrmRouting = (
  companyId: string,
  data: UpdateCompanyCrmRoutingRequestDto
) =>
  createFrontOfficeInstance<void>({
    url: `/api/companies/${companyId}/crm-routing`,
    method: 'PUT',
    data,
  });

export const updateCompanyCrmUserMapping = (
  companyId: string,
  crmUserId: string,
  data: UpdateCompanyCrmUserMappingRequestDto
) =>
  createFrontOfficeInstance<void>({
    url: `/api/companies/${companyId}/crm-users/${encodeURIComponent(crmUserId)}/mapping`,
    method: 'PUT',
    data,
  });
